import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { classNames } from "@/utils/classNames"
import { useMemo, useRef, useState } from "react"
import * as XLSX from "xlsx"

interface SpreadsheetPreviewProps {
  data: ArrayBuffer
}

const MAX_ROWS = 500
const MAX_COLS = 50

type SheetRow = unknown[]

interface SheetCell {
  text: string
  /** From the underlying cell's Excel data type (`t === "n"`), not the
   * formatted text — a `000`-formatted id is still numeric and stays
   * right-aligned even though its text looks like a string. */
  numeric: boolean
}

interface SheetTable {
  rows: SheetCell[][]
  columnCount: number
  /** 1-based Excel row number of the first row in `rows`. */
  startRowNumber: number
  /** 0-based sheet column index of the first column in `rows`, for
   * rendering the correct column letter (a sheet whose used range starts
   * at column C must not be relabeled A). */
  startColIndex: number
  rowsTruncated: boolean
  columnsTruncated: boolean
}

const EMPTY_TABLE: SheetTable = {
  rows: [],
  columnCount: 0,
  startRowNumber: 1,
  startColIndex: 0,
  rowsTruncated: false,
  columnsTruncated: false,
}

// Bounds the read itself via SheetJS's `range` option (computed from the
// sheet's own `!ref` extent) rather than reading the full sheet into JSON
// and slicing afterwards — a sheet with hundreds of thousands of rows would
// otherwise be fully materialized just to keep the first 500 of them.
function tableFromWorksheet(worksheet: XLSX.WorkSheet): SheetTable {
  const ref = worksheet["!ref"]
  if (!ref) return EMPTY_TABLE

  // `sheetRows` at read time keeps only the first MAX_ROWS rows in memory;
  // SheetJS then records the sheet's true extent in `!fullref`, which is what
  // decides whether to say rows were left out.
  const fullRange = XLSX.utils.decode_range(
    (worksheet as { "!fullref"?: string })["!fullref"] ?? ref,
  )
  const totalRows = fullRange.e.r - fullRange.s.r + 1
  const totalCols = fullRange.e.c - fullRange.s.c + 1
  const rowsTruncated = totalRows > MAX_ROWS
  const columnsTruncated = totalCols > MAX_COLS

  const cappedRange = {
    s: { r: fullRange.s.r, c: fullRange.s.c },
    e: {
      r: Math.min(fullRange.e.r, fullRange.s.r + MAX_ROWS - 1),
      c: Math.min(fullRange.e.c, fullRange.s.c + MAX_COLS - 1),
    },
  }
  const columnCount = cappedRange.e.c - cappedRange.s.c + 1

  // `raw: false` returns each cell's formatted text (its `w`) instead of the
  // raw underlying value — an id with a `000` format, a `0%` percentage or a
  // date all render as the workbook actually displays them, not as a bare
  // number.
  const formattedRows = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
    header: 1,
    range: cappedRange,
    raw: false,
  })

  const rows: SheetCell[][] = formattedRows.map((formattedRow, rowOffset) => {
    const sheetRow = cappedRange.s.r + rowOffset
    return Array.from({ length: columnCount }, (_, colOffset) => {
      const sheetCol = cappedRange.s.c + colOffset
      const cellRef = XLSX.utils.encode_cell({ r: sheetRow, c: sheetCol })
      const cell = worksheet[cellRef] as XLSX.CellObject | undefined
      const value = formattedRow[colOffset]
      return {
        text: value === null || value === undefined ? "" : String(value),
        numeric: cell?.t === "n",
      }
    })
  })

  return {
    rows,
    columnCount,
    startRowNumber: cappedRange.s.r + 1,
    startColIndex: cappedRange.s.c,
    rowsTruncated,
    columnsTruncated,
  }
}

/** Renders an xlsx/xlsm/xls/csv workbook read from bytes: quiet pill tabs per
 * sheet, spreadsheet-style column letters and row numbers (every worksheet
 * row is data — none of them is treated as a header), and a note when the
 * sheet was too large to show in full. Bytes are trusted to already be a
 * spreadsheet — the caller (`previewKind`) routes only matching files
 * here. */
export const SpreadsheetPreview: React.FC<SpreadsheetPreviewProps> = ({
  data,
}) => {
  const { translate } = useTranslation()

  const workbook = useMemo(() => {
    try {
      const parsed = XLSX.read(data, { type: "array", sheetRows: MAX_ROWS })
      return parsed.SheetNames.length > 0 ? parsed : null
    } catch {
      return null
    }
  }, [data])

  const sheetNames = workbook?.SheetNames ?? []
  const [activeSheet, setActiveSheet] = useState(0)
  // Roving tabindex: only the active tab is Tab-reachable; arrow keys move
  // focus (and, per the standard tabs pattern, selection) among the rest.
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | null = null
    if (event.key === "ArrowRight") {
      nextIndex = (index + 1) % sheetNames.length
    } else if (event.key === "ArrowLeft") {
      nextIndex = (index - 1 + sheetNames.length) % sheetNames.length
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = sheetNames.length - 1
    }
    if (nextIndex === null) return
    event.preventDefault()
    setActiveSheet(nextIndex)
    tabRefs.current[nextIndex]?.focus()
  }

  // A reopened preview for a different workbook must not keep showing
  // whatever sheet index was last selected — sheet 0 of the new file, not
  // sheet 3 of the old one. Reset synchronously during render (React's
  // documented pattern for "adjust state when a prop changes") rather than
  // in a useEffect, so the very first render for new bytes already reflects
  // it.
  const lastDataRef = useRef<ArrayBuffer | null>(null)
  if (lastDataRef.current !== data) {
    lastDataRef.current = data
    setActiveSheet(0)
  }

  const sheetName = sheetNames[activeSheet] ?? sheetNames[0]

  const table = useMemo(() => {
    if (!workbook || !sheetName) return null
    const worksheet = workbook.Sheets[sheetName]
    if (!worksheet) return null
    return tableFromWorksheet(worksheet)
  }, [workbook, sheetName])

  if (!workbook || !table) {
    return (
      <p className="text-[14px]/[1.2] text-grey5">
        {translate("DATA_ROOM/PREVIEW_ERROR")}
      </p>
    )
  }

  const columnIndexes = Array.from({ length: table.columnCount }, (_, i) => i)

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      {sheetNames.length > 1 && (
        <div
          role="tablist"
          aria-label={translate("DATA_ROOM/PREVIEW_SHEET_TABS_ARIA")}
          className="flex flex-wrap gap-1"
        >
          {sheetNames.map((name, index) => (
            <button
              key={name}
              ref={(el) => {
                tabRefs.current[index] = el
              }}
              type="button"
              role="tab"
              aria-selected={index === activeSheet}
              tabIndex={index === activeSheet ? 0 : -1}
              onClick={() => setActiveSheet(index)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              className={classNames(
                "rounded-full px-3 py-1 text-xs transition-colors",
                index === activeSheet
                  ? "bg-grey2 text-white"
                  : "text-grey5 hover:text-white",
              )}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto rounded border border-grey4/60">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-grey1">
            <tr>
              <th className="border-b border-grey4/60 px-3 py-2 text-left font-medium text-grey5" />
              {columnIndexes.map((colOffset) => (
                <th
                  key={colOffset}
                  className="whitespace-nowrap border-b border-grey4/60 px-3 py-2 text-left font-medium text-white"
                >
                  {XLSX.utils.encode_col(table.startColIndex + colOffset)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowOffset) => (
              <tr
                key={rowOffset}
                className="border-b border-grey4/60 last:border-b-0"
              >
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-grey5">
                  {table.startRowNumber + rowOffset}
                </td>
                {row.map((cell, colOffset) => (
                  <td
                    key={colOffset}
                    className={classNames(
                      "whitespace-nowrap px-3 py-1.5 text-grey6",
                      cell.numeric && "text-right tabular-nums",
                    )}
                  >
                    {cell.text}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(table.rowsTruncated || table.columnsTruncated) && (
        <div className="flex flex-col gap-0.5">
          {table.rowsTruncated && (
            <p className="text-xs text-grey5">
              {translate("DATA_ROOM/PREVIEW_ROWS_LIMITED", {
                count: MAX_ROWS,
              })}
            </p>
          )}
          {table.columnsTruncated && (
            <p className="text-xs text-grey5">
              {translate("DATA_ROOM/PREVIEW_COLUMNS_LIMITED", {
                count: MAX_COLS,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
